import type { TipusNode } from '../marc/estructura';

export interface Seleccio {
  tipus: TipusNode;
  id: number;
}

/** Clau d'un node a l'URL i a l'arbre: «DEPARTAMENT-2», «AREA-1», «SERVEI-10». */
export const clauNode = (s: Seleccio) => `${s.tipus}-${s.id}`;

export function llegeixClau(v: string | null): Seleccio | null {
  const m = /^(DEPARTAMENT|AREA|SERVEI)-(\d+)$/.exec(v ?? '');
  return m ? { tipus: m[1] as TipusNode, id: Number(m[2]) } : null;
}

const PARAM: Record<TipusNode, string> = {
  DEPARTAMENT: 'departament',
  AREA: 'area',
  SERVEI: 'servei',
};

/**
 * Node triat a partir de l'URL: `?servei=10`, `?area=1` o `?departament=2` (els enllaços des
 * d'altres pantalles) o el format `?node=SERVEI-10`.
 */
export function seleccioDeParams(params: URLSearchParams): Seleccio | null {
  for (const tipus of ['SERVEI', 'AREA', 'DEPARTAMENT'] as TipusNode[]) {
    const v = params.get(PARAM[tipus]);
    if (v && /^\d+$/.test(v)) return { tipus, id: Number(v) };
  }
  return llegeixClau(params.get('node'));
}

/** Paràmetres d'URL per a un node (`{ servei: '10' }`). */
export const paramsDeSeleccio = (s: Seleccio) => ({ [PARAM[s.tipus]]: String(s.id) });

/** Tipus del fill que es crea des d'un node (els serveis no en tenen). */
export const TIPUS_FILL: Partial<Record<TipusNode, TipusNode>> = {
  DEPARTAMENT: 'AREA',
  AREA: 'SERVEI',
};

export const normalitza = (s: string) =>
  s
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase();

/**
 * Codi suggerit a partir del nom: una paraula → les 6 primeres lletres; diverses → les 3
 * primeres de la primera i la inicial de les següents (sense articles curts). «Xarxa de
 * botigues» → «XARB».
 */
export function suggereixCodi(nom: string): string {
  const paraules = normalitza(nom)
    .toUpperCase()
    .split(/[^A-Z0-9]+/)
    .filter((p) => p.length > 2);
  if (paraules.length === 0) return '';
  if (paraules.length === 1) return (paraules[0] ?? '').slice(0, 6);
  return (
    (paraules[0] ?? '').slice(0, 3) +
    paraules
      .slice(1)
      .map((p) => p[0])
      .join('')
  ).slice(0, 6);
}

/** Nombre de camps diferents entre l'original i l'edició (per a la barra de canvis). */
export function campsCanviats<T extends object>(original: T, edicio: T, camps: (keyof T)[]) {
  const norm = (v: unknown) =>
    Array.isArray(v) ? [...v].sort().join(',') : v == null ? '' : String(v);
  return camps.filter((c) => norm(original[c]) !== norm(edicio[c])).length;
}
